'use client';
// Единый провайдер описаний для всех тестов.
// Один запрос /api/assessment/descriptions/all при загрузке ЛК.
// Все компоненты берут описания из контекста — без отдельных fetch.
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { fetchWithToken } from '../api-core';

type Gradation = 'low' | 'mid' | 'high';
type DescEntry = Record<Gradation, { title?: string; summary?: string; work?: string; people?: string; recommendation?: string }>;
type TestDescs = Record<string, DescEntry>; // aspectKey → gradations
type AllDescs = Record<string, TestDescs>; // testSlug → aspects

type Ctx = {
  /** Все описания: testSlug → { aspectKey → { gradation → desc } } */
  all: AllDescs;
  loaded: boolean;
  /** Получить описание для конкретного аспекта */
  get: (testSlug: string, aspectKey: string, gradation: Gradation) => { title?: string; summary?: string; work?: string; people?: string; recommendation?: string } | undefined;
};

const DescCtx = createContext<Ctx>({ all: {}, loaded: false, get: () => undefined });

export function DescriptionsProvider({ children }: { children: ReactNode }) {
  const [all, setAll] = useState<AllDescs>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const json: any = await fetchWithToken('/api/assessment/descriptions/all');
        if (active && json?.data) {
          setAll(json.data);
          setLoaded(true);
        }
      } catch (e) {
        console.warn('[Descriptions] load failed', e);
        setLoaded(true); // не блокируем рендер — покажем без описаний
      }
    })();
    return () => { active = false; };
  }, []);

  const get: Ctx['get'] = (testSlug, aspectKey, gradation) =>
    all[testSlug]?.[aspectKey]?.[gradation];

  return <DescCtx.Provider value={{ all, loaded, get }}>{children}</DescCtx.Provider>;
}

/** Хук для доступа ко всем описаниям */
export function useDescriptions() {
  return useContext(DescCtx);
}

/** Хук для описаний конкретного теста */
export function useTestDescriptions(testSlug: string | undefined) {
  const { all, loaded } = useDescriptions();
  if (!testSlug) return { descs: {}, loaded };
  return { descs: all[testSlug] ?? {}, loaded };
}
